/**
 * Supported sports on the platform.
 * Each sport drives: UI literals, match rules, player positions, and category structure.
 */
export type SportType = 'fronton' | 'remo' | 'other';

/** Display names for each sport (used in super-admin UI) */
export const SPORT_LABELS: Record<SportType, string> = {
  fronton: 'Frontón / Pelota',
  remo: 'Remo',
  other: 'Otro',
};

// ─── Configurable dropdown option ────────────────────────────────────────────

/** A single selectable option for sport-specific dropdowns (scopes, modalities…). */
export interface SportOption {
  value: string;
  label: string;
}

// ─── Sport-specific UI labels ─────────────────────────────────────────────────

/**
 * Human-readable labels for key entities, adapted per sport.
 * Used throughout the admin panel and mobile app to replace hardcoded Spanish.
 */
export interface SportLabels {
  /** e.g. "Jugador/a" | "Remero/a" */
  player: string;
  /** e.g. "Jugadores/as" | "Remeros/as" */
  players: string;
  /** e.g. "Equipo" | "Tripulación" */
  team: string;
  /** e.g. "Equipos" | "Tripulaciones" */
  teams: string;
  /** e.g. "Competición" | "Liga" */
  competition: string;
  /** e.g. "Competiciones" | "Ligas" */
  competitions: string;
  /** e.g. "Partido" | "Regata" */
  match: string;
  /** e.g. "Partidos" | "Regatas" */
  matches: string;
  /** e.g. "Árbitro" | "Juez" */
  referee: string;
  /** e.g. "Árbitros" | "Jueces" */
  referees: string;
  /** Label for a federated person: "Federado/a" | "Remero/a" */
  federated: string;
  /** Plural: "Federados/as" | "Remeros/as" */
  federateds: string;
  /** Name for the scope/discipline dimension: "Modalidad" | "Modalidad" */
  scope: string;
  /** Label for one competition round: "Jornada" | "Jornada" */
  round: string;
  /** Label for the club: "Club" | "Club" */
  club: string;
  /** Label plural: "Clubs" | "Clubs" */
  clubs: string;
  /** e.g. "Instalación" | "Barco" | "Pista" */
  facility: string;
  /** e.g. "Instalaciones" | "Barcos" | "Pistas" */
  facilities: string;
}

// ─── Default labels per sport ─────────────────────────────────────────────────

/**
 * Canonical label sets for each supported sport.
 * Components should consume these via `TenantFeaturesService.sportLabels$`
 * rather than reading them directly — the service derives the right entry
 * from the tenant's configured `sport` field.
 */
export const SPORT_LABELS_MAP: Record<SportType, SportLabels> = {
  fronton: {
    player: 'Jugador/a',
    players: 'Jugadores/as',
    team: 'Equipo',
    teams: 'Equipos',
    competition: 'Competición',
    competitions: 'Competiciones',
    match: 'Partido',
    matches: 'Partidos',
    referee: 'Árbitro',
    referees: 'Árbitros',
    federated: 'Federado/a',
    federateds: 'Federados/as',
    scope: 'Modalidad',
    round: 'Jornada',
    club: 'Club',
    clubs: 'Clubs',
    facility: 'Instalación',
    facilities: 'Instalaciones',
  },
  remo: {
    player: 'Remero/a',
    players: 'Remeros/as',
    team: 'Tripulación',
    teams: 'Tripulaciones',
    competition: 'Liga',
    competitions: 'Ligas',
    match: 'Regata',
    matches: 'Regatas',
    referee: 'Juez',
    referees: 'Jueces',
    federated: 'Remero/a',
    federateds: 'Remeros/as',
    scope: 'Modalidad',
    round: 'Jornada',
    club: 'Club',
    clubs: 'Clubs',
    facility: 'Barco',
    facilities: 'Barcos',
  },
  other: {
    player: 'Jugador/a',
    players: 'Jugadores/as',
    team: 'Equipo',
    teams: 'Equipos',
    competition: 'Competición',
    competitions: 'Competiciones',
    match: 'Partido',
    matches: 'Partidos',
    referee: 'Árbitro',
    referees: 'Árbitros',
    federated: 'Participante',
    federateds: 'Participantes',
    scope: 'Modalidad',
    round: 'Jornada',
    club: 'Club',
    clubs: 'Clubs',
    facility: 'Instalación',
    facilities: 'Instalaciones',
  },
};

// ─── Sport-specific UI flags ──────────────────────────────────────────────────

/**
 * Feature flags derived from the sport config.
 * Drive show/hide of UI sections and form fields.
 */
export interface SportUIFlags {
  /** Whether the sport uses front/back player positions. */
  hasPositions: boolean;
  /** Racing format (N boats, result by time/position) vs 1v1 game format. */
  isRaceFormat: boolean;
  /** Whether to show game/set score breakdown in match detail. */
  showGameScore: boolean;
  /** Whether licenses carry a position (delantero/zaguero). */
  hasLicensePositions: boolean;
  /** Whether the sport supports player transfers between clubs. */
  hasTransfers: boolean;
}

/**
 * Sport-specific configuration stored inside TenantConfig.
 * Drives match rules, player labels, and category logic.
 */
export interface SportConfig {
  /** Slug of the sport */
  sport: SportType;

  /** How many sets to win a game (default: 2) */
  setsToWinGame: number;

  /** How many games to win a match (default: 3 for best-of-5) */
  gamesToWinMatch: number;

  /** Custom label for a single player position (e.g. "Zaguero", "Drive", etc.) */
  positionLabels: {
    front: string; // e.g. "Delantero" / "Revés"
    back: string; // e.g. "Zaguero" / "Drive"
  };

  /** Label used for a "game" unit within the match (e.g. "Juego", "Set", "Período") */
  gameUnitLabel: string;

  /** Label used for a "set" within a game (e.g. "Set", "Parcial") */
  setUnitLabel: string;

  /** Whether the sport uses player positions (front/back) — false for individual sports */
  hasPositions: boolean;

  /** Whether the sport is played in pairs (doubles) */
  isPairSport: boolean;

  /**
   * Whether the sport uses a race/time-based result format (multiple competitors
   * racing simultaneously) instead of a 1v1 game/set format.
   * true = rowing, cycling, athletics
   * false = frontenis, padel, tennis
   */
  isRaceFormat: boolean;

  /**
   * Whether ball tracking (used balls / broken balls) is relevant for this sport.
   * true = frontenis (pelota vasca, where balls break and are counted per game)
   * false = most other sports
   */
  trackBalls: boolean;

  /**
   * Entity and action labels for the admin panel and mobile app.
   * Overrides generic terms with sport-specific ones (e.g. "Equipo" → "Tripulación").
   */
  labels: SportLabels;

  /**
   * Scopes/disciplines available for this sport.
   * Replaces the hardcoded Scope enum in dropdown options.
   * e.g. Frontenis: [{value:'MIXTAS', label:'Mixtas'}, …]
   *      Remo:     [{value:'BANCO_FIJO', label:'Banco Fijo'}, …]
   */
  availableScopes: SportOption[];

  /**
   * Modalities/sub-disciplines available for this sport.
   * e.g. Frontenis: [{value:'OLIMPICA', label:'Olímpica'}, …]
   *      Remo:     [{value:'INDIVIDUAL', label:'Individual'}, …]
   */
  availableModalities: SportOption[];

  /**
   * Competition types available for this sport (label overrides for CompetitionType).
   * Replaces generic "Liga / Copa / JE" labels with sport-specific ones.
   * Key = CompetitionType value, value = display label.
   */
  competitionTypeLabels: Record<string, string>;

  /**
   * Round-robin calendar generation: whether the team occupying the LAST
   * position of the season's team list (the pivot of the Berger rotation)
   * plays its first match AT HOME instead of away.
   *
   * `false` (default) keeps the historical behaviour: the pivot starts away.
   * `true` matches the convention of the fmpelota federation spreadsheets.
   * Only the home/away side of that team's n-1 matches changes; the pairings
   * of each round are identical either way.
   *
   * This is the TENANT-WIDE DEFAULT. Each season stores its own
   * `lastPositionStartsHome`, seeded from this value, and generation always
   * reads the season's copy so a calendar stays reproducible over the years.
   */
  lastPositionStartsHome?: boolean;

  /** Brevo template IDs for transactional emails — keyed by event name */
  emailTemplates: Partial<SportEmailTemplates>;

  /**
   * Points awarded per exact match scoreline, used by seasons whose
   * `playerRankingMode === 'result'`. Keys are `"<setsWon>-<setsLost>"`
   * (e.g. `"2-0"`, `"2-1"`, `"1-2"`, `"0-2"`), values are the points.
   * A scoreline missing from the table awards 0 points.
   * Configured per sport; when absent, result-mode rankings award 0.
   */
  playerRankingResultTable?: Record<string, number>;
}

/** Brevo transactional email template IDs for sport-specific events */
export interface SportEmailTemplates {
  /** Sent when a new tenant admin is invited to onboard */
  tenantAdminInvite: number;
  /** Sent when a player receives a license */
  licenseGranted: number;
  /** Sent when a match result is published */
  matchResult: number;
  /** Sent when a transfer request is created */
  transferRequest: number;
  /** Sent when a player is sanctioned */
  sanctionNotification: number;
}

// ─── Default sport configurations ────────────────────────────────────────────

/** Default sport configs — used as starting point when a tenant selects a sport */
export const DEFAULT_SPORT_CONFIGS: Record<SportType, Omit<SportConfig, 'emailTemplates'>> = {
  fronton: {
    sport: 'fronton',
    setsToWinGame: 2,
    gamesToWinMatch: 3,
    positionLabels: { front: 'Delantero', back: 'Zaguero' },
    gameUnitLabel: 'Partido',
    setUnitLabel: 'Set',
    hasPositions: true,
    isPairSport: true,
    isRaceFormat: false,
    trackBalls: true,
    lastPositionStartsHome: false,
    labels: {
      player: 'Jugador/a',
      players: 'Jugadores/as',
      team: 'Equipo',
      teams: 'Equipos',
      competition: 'Competición',
      competitions: 'Competiciones',
      match: 'Partido',
      matches: 'Partidos',
      referee: 'Árbitro',
      referees: 'Árbitros',
      federated: 'Federado/a',
      federateds: 'Federados/as',
      scope: 'Modalidad',
      round: 'Jornada',
      club: 'Club',
      clubs: 'Clubs',
      facility: 'Instalación',
      facilities: 'Instalaciones',
    },
    availableScopes: [
      { value: 'MIXTAS', label: 'Mixtas' },
      { value: 'AFICIONADO', label: 'Aficionado' },
      { value: 'PELOTA_LENTA', label: 'Pelota Lenta' },
      { value: 'PALETA_CUERO', label: 'Paleta Cuero' },
      { value: 'PALA_CORTA', label: 'Pala Corta' },
      { value: 'MANO', label: 'Mano' },
    ],
    availableModalities: [
      { value: 'PREOLIMPICA', label: 'Preolímpica' },
      { value: 'OLIMPICA', label: 'Olímpica' },
      { value: 'PALETA-GOMA', label: 'Paleta Goma' },
      { value: 'PALETA-CUERO', label: 'Paleta Cuero' },
      { value: 'PALA-CORTA', label: 'Pala Corta' },
      { value: 'FRONT-BALL', label: 'Front Ball' },
      { value: 'MANO', label: 'Mano' },
      { value: 'PREOLIMPICA-INDIVIDUAL', label: 'Preolímpica Individual' },
      { value: 'OLIMPICA-INDIVIDUAL', label: 'Olímpica Individual' },
      { value: 'PALETA-GOMA-INDIVIDUAL', label: 'Paleta Goma Individual' },
    ],
    competitionTypeLabels: {
      LEAGUE: 'Liga',
      CUP: 'Copa',
    },
    // Default result-mode scoring for frontenis (FFPCV Art. 12 a–d). Seasons may
    // override this per-competition from the admin panel.
    playerRankingResultTable: { '2-0': 6, '2-1': 5, '1-2': 2, '0-2': 1 },
  },
  remo: {
    sport: 'remo',
    setsToWinGame: 1,
    gamesToWinMatch: 1,
    positionLabels: { front: 'Proa', back: 'Coda' },
    gameUnitLabel: 'Regata',
    setUnitLabel: 'Manga',
    hasPositions: false,
    isPairSport: false,
    isRaceFormat: true,
    trackBalls: false,
    lastPositionStartsHome: false,
    labels: {
      player: 'Remero/a',
      players: 'Remeros/as',
      team: 'Tripulación',
      teams: 'Tripulaciones',
      competition: 'Liga',
      competitions: 'Ligas',
      match: 'Regata',
      matches: 'Regatas',
      referee: 'Juez',
      referees: 'Jueces',
      federated: 'Remero/a',
      federateds: 'Remeros/as',
      scope: 'Modalidad',
      round: 'Jornada',
      club: 'Club',
      clubs: 'Clubs',
      facility: 'Barco',
      facilities: 'Barcos',
    },
    availableScopes: [
      { value: 'BANCO_FIJO', label: 'Banco Fijo' },
      { value: 'LLAUT', label: 'Llaüt Mediterráneo' },
      { value: 'REMO_OLIMPICO', label: 'Remo Olímpico' },
      { value: 'ERGOMETRO', label: 'Remoergómetro' },
    ],
    availableModalities: [
      { value: 'INDIVIDUAL', label: 'Individual' },
      { value: 'PAREJAS', label: 'Parejas' },
      { value: 'EQUIPO', label: 'Equipo' },
    ],
    competitionTypeLabels: {
      LEAGUE: 'Liga',
      CUP: 'Campeonato Autonómico',
      JE: 'Campeonato Nacional',
    },
  },
  other: {
    sport: 'other',
    setsToWinGame: 1,
    gamesToWinMatch: 1,
    positionLabels: { front: 'Jugador A', back: 'Jugador B' },
    gameUnitLabel: 'Partido',
    setUnitLabel: 'Período',
    hasPositions: false,
    isPairSport: false,
    isRaceFormat: false,
    trackBalls: false,
    lastPositionStartsHome: false,
    labels: {
      player: 'Jugador/a',
      players: 'Jugadores/as',
      team: 'Equipo',
      teams: 'Equipos',
      competition: 'Competición',
      competitions: 'Competiciones',
      match: 'Partido',
      matches: 'Partidos',
      referee: 'Árbitro',
      referees: 'Árbitros',
      federated: 'Federado/a',
      federateds: 'Federados/as',
      scope: 'Categoría',
      round: 'Jornada',
      club: 'Club',
      clubs: 'Clubs',
      facility: 'Instalación',
      facilities: 'Instalaciones',
    },
    availableScopes: [],
    availableModalities: [],
    competitionTypeLabels: {
      LEAGUE: 'Liga',
      CUP: 'Copa',
      JE: 'Juegos',
    },
  },
};
